import React, { useState, useLayoutEffect, useCallback, useMemo } from 'react';
import ThemeContext from './index';

const STORAGE_KEY = 'theme';

/** Stored preference wins; otherwise follow the OS setting. */
const getInitialTheme = () => {
    if (typeof window === 'undefined') return 'light';

    const storedTheme = localStorage.getItem(STORAGE_KEY);
    if (storedTheme === 'dark' || storedTheme === 'light') {
        return storedTheme;
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(getInitialTheme);

    // Applied before paint so the swap lands in the same frame as the click.
    // `dark-mode` is the legacy class used by the pre-shadcn CSS; `dark` is what
    // Tailwind and shadcn key on. Both are set until the migration is complete.
    useLayoutEffect(() => {
        const root = document.documentElement;
        root.classList.toggle('dark-mode', theme === 'dark');
        root.classList.toggle('dark', theme === 'dark');
        root.style.colorScheme = theme;
        localStorage.setItem(STORAGE_KEY, theme);
    }, [theme]);

    // Follow the OS only while the user has not made an explicit choice.
    useLayoutEffect(() => {
        const query = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (!query) return undefined;

        const onChange = (event) => {
            if (localStorage.getItem(STORAGE_KEY)) return;
            setTheme(event.matches ? 'dark' : 'light');
        };
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    const toggleTheme = useCallback(() => {
        const root = document.documentElement;
        // Only crossfade on an intentional toggle, never on first paint.
        root.classList.add('theme-transition');
        window.setTimeout(() => root.classList.remove('theme-transition'), 250);
        setTheme((current) => (current === 'light' ? 'dark' : 'light'));
    }, []);

    const value = useMemo(() => ({ theme, toggleTheme, setTheme }), [theme, toggleTheme]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
